import React, { useMemo, useState } from 'react';
import './PromptAnalysisHistory.scss';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Skeleton } from 'primereact/skeleton';
import { format, isToday, isYesterday } from 'date-fns';
import { formatLongDate } from '../../../shared/helpers/date/formatLongDate';
import SimpleBar from 'simplebar-react';
import type { PromptAnalysis, PromptAnalysisStatus } from '@brand24-original/llm-types';
import CustomTooltip from '../../../shared/components/common/tooltip/CustomTooltip';

/** Like ChatGPT's list: only the prompt, one line. A status shows as a small icon at the end. */
const STATUS_ICON: Partial<Record<PromptAnalysisStatus, string>> = {
  PENDING: 'ico--loader',
  RUNNING: 'ico--loader',
  FAILED: 'ico--warning',
};

interface PromptAnalysisHistoryProps {
  /** Newest first, as the endpoint returns them; undefined while loading. */
  analyses: PromptAnalysis[] | undefined;
  selectedId: number | null;
  onSelect: (id: number) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  /** Back to the new-analysis screen (like ChatGPT's "New chat"). */
  onNew: () => void;
}

const PromptAnalysisHistory: React.FC<PromptAnalysisHistoryProps> = ({
  analyses,
  selectedId,
  onSelect,
  isCollapsed,
  onToggleCollapse,
  onNew,
}) => {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');

  const groups = useMemo(() => {
    const query = search.trim().toLowerCase();
    const byDay = new Map<string, PromptAnalysis[]>();
    for (const analysis of analyses ?? []) {
      if (query && !analysis.prompt.toLowerCase().includes(query)) continue;
      const date = new Date(analysis.requestedAt);
      const label = isToday(date)
        ? t('promptAnalyst.today')
        : isYesterday(date)
        ? t('promptAnalyst.yesterday')
        : formatLongDate(date, i18n.language);
      byDay.set(label, [...(byDay.get(label) ?? []), analysis]);
    }
    return Array.from(byDay);
  }, [analyses, search, t, i18n.language]);

  return (
    <aside
      className={`prompt-analysis-history${isCollapsed ? ' prompt-analysis-history--collapsed' : ''}`}
    >
      <div className="prompt-analysis-history__header">
        {/* Ten sam przycisk co zwijanie lewego menu (`Panel`), w lustrzanym kierunku. */}
        <Button
          id="btn-toggle-history"
          className="action-btn prompt-analysis-history__toggle"
          onClick={onToggleCollapse}
        >
          <span className="ico--right ico--sm" />
        </Button>
        <CustomTooltip
          tooltipText={isCollapsed ? 'promptAnalyst.expandHistory' : 'promptAnalyst.collapseHistory'}
          targetRef="btn-toggle-history"
        />
        <div className="page-box__title prompt-analysis-history__title">
          {t('promptAnalyst.historyTitle')}
        </div>
        {/* Zwinięty panel zostawia sam plus — nowa analiza zawsze o jedno kliknięcie. */}
        {isCollapsed && (
          <>
            <Button
              id="btn-new-analysis"
              className="action-btn prompt-analysis-history__toggle"
              onClick={onNew}
            >
              <span className="ico--plus ico--sm" />
            </Button>
            <CustomTooltip tooltipText="promptAnalyst.newAnalysis" targetRef="btn-new-analysis" />
          </>
        )}
      </div>
      <div className="prompt-analysis-history__body">
        <Button type="button" className="btn btn--sm prompt-analysis-history__new" onClick={onNew}>
          <span className="ico--plus ico--xs ico--white" />
          {t('promptAnalyst.newAnalysis')}
        </Button>
        <div className="input-search-box prompt-analysis-history__search">
          <InputText
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('promptAnalyst.searchHistory')}
            className="input-search"
          />
        </div>
        {/* Przewija się sama lista — „Nowa analiza" i wyszukiwarka zostają na górze panelu. */}
        <SimpleBar className="prompt-analysis-history__scroll" autoHide={false}>
          {!analyses ? (
            <Skeleton className="skeleton--table">
              <span />
              <span />
              <span />
            </Skeleton>
          ) : (
            groups.length === 0 && (
              <p className="prompt-analysis-history__empty">{t('promptAnalyst.historyEmpty')}</p>
            )
          )}
          {groups.map(([label, items]) => (
            <div key={label} className="prompt-analysis-history__group">
              <div className="prompt-analysis-history__day">{label}</div>
              {items.map((analysis) => {
                const statusIcon = STATUS_ICON[analysis.status];
                return (
                  <button
                    key={analysis.id}
                    type="button"
                    className={`prompt-analysis-history__item${
                      analysis.id === selectedId ? ' prompt-analysis-history__item--active' : ''
                    }`}
                    onClick={() => onSelect(analysis.id)}
                    title={analysis.prompt}
                  >
                    <span className="prompt-analysis-history__text">
                      <span className="prompt-analysis-history__prompt">{analysis.prompt}</span>
                      <span className="prompt-analysis-history__date">
                        {format(new Date(analysis.requestedAt), 'yyyy-MM-dd HH:mm')}
                      </span>
                    </span>
                    {statusIcon && (
                      <span
                        className={`${statusIcon} ico--xs`}
                        aria-label={t(`promptAnalyst.status.${analysis.status}`)}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </SimpleBar>
      </div>
    </aside>
  );
};

export default PromptAnalysisHistory;
